import React, { useState, useMemo, useCallback } from "react";
import ClassNames from "classnames";
import "./InlineInput.scss";
import { Tooltip } from "../../components";
import { FormFieldError } from "../../controls";
import { getDataAnalyticsProps } from "../../../utils";
import { useCopyToClipboard } from "../../../hooks";
import IconButton from "../Buttons/IconButton/IconButton";

type InlineInputSize =
  | "xxs"
  | "xs"
  | "s"
  | "m"
  | "l"
  | "xl"
  | "xxl"
  | "max"
  | "custom";

export interface InlineInputProps {
  "data-testid"?: string;
  wrapperClassName?: string;
  className?: string;
  type?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  error?: { message?: string; type?: string };
  label?: string;
  labelTop?: boolean;
  size?: InlineInputSize;
  required?: boolean;
  hideLabelHelper?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  password?: boolean;
  copyable?: boolean;
  copyLabel?: string;
  inputWidth?: number | string | boolean;
  isInline?: boolean;
  isLarge?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  leftIcon?: string;
  rightIcon?: string;
  isClearable?: boolean;
  suffixIconButton?: React.ReactNode;
  tooltipData?: { dataTip?: string };
  min?: number | null;
  max?: number | null;
  inputRef?: React.Ref<HTMLInputElement>;
  title?: string;
  noMargin?: boolean;
  withHelp?: string;
  autoComplete?: string;
  hideError?: boolean;
  description?: React.ReactNode;
  labelRight?: React.ReactNode;
  applyAutoFocus?: boolean;
  [key: string]: any;
}

const InlineInput: React.FC<InlineInputProps> = (props) => {
  const [isFocused, setIsFocused] = useState(false);
  const {
    type = "text",
    value,
    onChange = () => {},
    name,
    error,
    wrapperClassName,
    label,
    labelTop = false,
    size = "max",
    required = false,
    hideLabelHelper = false,
    disabled = false,
    readOnly = false,
    password = false,
    copyable = false,
    copyLabel = "",
    inputWidth = false,
    isInline,
    isLarge,
    placeholder = "",
    onBlur = () => {},
    onFocus = () => {},
    leftIcon = "",
    rightIcon = "",
    isClearable,
    suffixIconButton = null,
    tooltipData = {},
    min = null,
    max = null,
    inputRef,
    title,
    noMargin = false,
    "data-testid": dataTestId,
    withHelp = "",
    autoComplete,
    hideError,
    description = "",
    labelRight = "",
    applyAutoFocus,
  } = props;

  const { copyLabel: copyTooltipLabel, copyText } = useCopyToClipboard({ defaultLabel: copyLabel });

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-inline-input",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-password": password,
      "has-read-only": readOnly,
      "has-left-icon": leftIcon,
      "has-right-icon": rightIcon,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-input-width": inputWidth,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "has-error": error,
      "with-help": withHelp?.length,
      "has-suffix-icon-btn": suffixIconButton || copyable,
      "is-copyable": copyable,
      "is-clearable": isClearable,
    },
    props.className
  );

  const onFocusChange = () => {
    if (isFocused) {
      if (onBlur) {
        onBlur();
      }
    } else {
      if (onFocus) {
        onFocus();
      }
    }
    setIsFocused(!isFocused);
  };

  const customProps = useMemo(() => {
    const inputProps: {
      min?: number;
      max?: number;
      minLength?: number;
      maxLength?: number;
    } = {};
    if (type === "number" && min != null && !isNaN(min)) {
      inputProps.min = min;
    }

    if (type === "number" && max != null && !isNaN(max)) {
      inputProps.max = max;
    }

    if (type === "text" && min != null && !isNaN(min)) {
      inputProps.minLength = min;
    }

    if (type === "text" && max != null && !isNaN(max)) {
      inputProps.maxLength = max;
    }

    return inputProps;
  }, [min, max, type]);

  const isValidInput = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const invalidChars = ["-", "+", "e", "E"];

      return type !== "number" ? true : !invalidChars.includes(e.key);
    },
    [type]
  );

  return (
    <div className={classNameWrapper}>
      {label && (
        <label className={`input-label`} data-testid={`${dataTestId}-label`}>
          {label}
        </label>
      )}

      {description && <div className={`input-description`}>{description}</div>}

      <Tooltip type="info" content={tooltipData?.dataTip}>
        <div
          className={`input-holder ${isFocused ? "is-focused" : ""}`}
          style={{ width: inputWidth ? (inputWidth as number | string) : "" }}
        >
          {leftIcon && (
            <i className={`input-left-icon seem-icon ${leftIcon}`} />
          )}
          <input
            autoFocus={applyAutoFocus}
            step={"any"}
            title={title}
            data-testid={`${dataTestId}-input`}
            ref={inputRef}
            readOnly={readOnly}
            placeholder={placeholder}
            name={name}
            type={type}
            disabled={disabled}
            required={required}
            value={value || ""}
            onChange={(e) => {
              onChange(e);
            }}
            onKeyDown={(e) => {
              if (!isValidInput(e)) {
                e.preventDefault();
              }
            }}
            onBlur={onFocusChange}
            onFocus={onFocusChange}
            autoComplete={autoComplete}
            {...customProps}
            {...getDataAnalyticsProps(props)}
          />

          {rightIcon && (
            <i className={`input-right-icon seem-icon ${rightIcon}`} />
          )}
          {suffixIconButton}
          {isClearable && !!value && (
            <IconButton
              isIcon={"input-right-icon seem-icon-close"}
              size={"xs"}
              type="button"
              onClick={() => {
                // Runtime passes a synthetic change-like object to clear the value.
                onChange({ target: { value: "" } } as unknown as React.ChangeEvent<HTMLInputElement>);
              }}
              data-testid={`${dataTestId}-clear`}
            />
          )}

          {!!copyable && !!value && (
            <IconButton
              isIcon={
                copyTooltipLabel === copyLabel
                  ? "seem-icon-copy"
                  : "seem-icon-jira-icon-done"
              }
              buttonStyle={"secondary"}
              type="button"
              withTooltip={{ content: copyTooltipLabel, className: "copy-tooltip" }}
              onClick={() => {
                copyText(value);
              }}
              data-testid={`${dataTestId}-copy`}
            />
          )}
        </div>
      </Tooltip>
      {labelRight && (
        <span className={`input-label input-label-right`}>{labelRight}</span>
      )}
      {withHelp && (
        <span className={"input-help-wrap"}>
          <>
            <Tooltip type="info" content={withHelp}>
              <i className={`seem-icon seem-icon-help`} />
            </Tooltip>
          </>
        </span>
      )}
      {error && !hideError && (
        <FormFieldError
          errorMessage={error.message}
          errorType={error.type}
          isOpen={!isFocused}
          fieldName={dataTestId}
        />
      )}
    </div>
  );
};

export default InlineInput;
